/**
 * Dashboard — what needs your attention, then what you were doing.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * The previous version led with four equally-weighted tiles in four unrelated
 * hues (blue "Choose Pipeline", green "View Jobs", purple "Recent Activity",
 * orange "Analytics") — navigation dressed as data, with green and amber
 * spent on links rather than on part verdicts. Two of the four went to the
 * same route.
 *
 * This leads with the only question the page can answer on arrival — is
 * anything waiting for me — and puts navigation underneath as plain rows.
 * Colour appears exactly three times, all of it status.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Briefcase, BarChart3, Wrench, FileText, ArrowRight } from 'lucide-react';
import { jobsAPI } from '../services/api';
import type { ProcessingJob } from '../types';
import { JobStatusBadge, jobStatusTone, TONE_EDGE } from '../components/JobStatusBadge';

const RECENT_LIMIT = 5;

/** Statuses that mean a person still has to do something. */
const NEEDS_REVIEW: ReadonlyArray<ProcessingJob['status']> = [
  'awaiting_validation',
  'completed_with_errors',
  'suspended',
];
const IN_FLIGHT: ReadonlyArray<ProcessingJob['status']> = ['processing', 'queued', 'pending'];

const QUICK_LINKS = [
  { label: 'New run', icon: LayoutGrid, to: '/select-pipeline' },
  { label: 'Jobs', icon: Briefcase, to: '/jobs' },
  { label: 'Analytics', icon: BarChart3, to: '/analytics' },
  { label: 'Data Utilities', icon: Wrench, to: '/data-utilities' },
];

const Dashboard: React.FC = () => {
  const { data: jobs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['jobs', 'dashboard'],
    queryFn: async () => {
      const response = await jobsAPI.list();
      const payload = response.data as ProcessingJob[] | { results?: ProcessingJob[] };
      return Array.isArray(payload) ? payload : payload.results ?? [];
    },
  });

  const reviewCount = jobs.filter((j) => NEEDS_REVIEW.includes(j.status)).length;
  const runningCount = jobs.filter((j) => IN_FLIGHT.includes(j.status)).length;
  const failedCount = jobs.filter((j) => j.status === 'failed').length;
  const recent = jobs.slice(0, RECENT_LIMIT);

  /* The reading strip. A count of zero stays on the page in a neutral tone
     rather than disappearing — "nothing needs review" is itself the answer,
     and a strip that changes shape between visits is harder to scan. */
  const readings = [
    { label: 'Needs review', value: reviewCount, tone: 'review' as const, to: '/jobs?filter=review' },
    { label: 'Running', value: runningCount, tone: 'active' as const, to: '/jobs?filter=running' },
    { label: 'Failed', value: failedCount, tone: 'fail' as const, to: '/jobs?filter=failed' },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <section aria-labelledby="attention-heading">
        <h2 id="attention-heading" className="mb-3 text-sm font-medium text-text-secondary">
          Needs your attention
        </h2>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {readings.map((r) => (
            <Link
              key={r.label}
              to={r.to}
              className={`status-edge ${r.value > 0 ? TONE_EDGE[r.tone] : ''} rounded-lg border border-border bg-bg-surface p-4 transition-colors hover:bg-bg-subtle`}
            >
              <p className="text-sm text-text-secondary">{r.label}</p>
              <p
                className={`metric-value mt-1 text-3xl font-semibold ${
                  r.value > 0
                    ? { review: 'qc-warning', active: 'qc-info', fail: 'qc-error' }[r.tone] : 'text-text-tertiary' }`}
              >
                {isLoading ? '—' : r.value}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="recent-heading">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="recent-heading" className="text-sm font-medium text-text-secondary">
            Recent jobs
          </h2>
          <Link
            to="/jobs"
            className="inline-flex items-center gap-1 text-sm font-medium text-interactive-primary hover:text-interactive-primary-hover"
          >
            View all
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-bg-surface">
          {isLoading ? (
            /* Skeleton rather than a spinner, so the row geometry is already
               correct when the data lands and nothing jumps. */
            <ul className="divide-y divide-border" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 p-4">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-bg-subtle" />
                  <div className="ml-auto h-5 w-20 animate-pulse rounded bg-bg-subtle" />
                </li>
              ))}
              <li className="sr-only">Loading recent jobs</li>
            </ul>
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="font-medium text-text-primary">Couldn&rsquo;t load your jobs</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 rounded-lg bg-interactive-primary px-4 py-2 text-sm font-medium text-text-inverse hover:bg-interactive-primary-hover"
              >
                Try again
              </button>
            </div>
          ) : recent.length === 0 ? (
            <div className="p-10 text-center">
              <FileText className="mx-auto mb-3 h-10 w-10 text-text-tertiary" aria-hidden="true" />
              <p className="font-medium text-text-primary">No jobs yet</p>
              <Link
                to="/select-pipeline"
                className="mt-4 inline-block rounded-lg bg-interactive-primary px-4 py-2 text-sm font-medium text-text-inverse hover:bg-interactive-primary-hover"
              >
                New run
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((job) => {
                const tone = jobStatusTone(job.status);
                return (
                  <li key={job.id}>
                    <Link
                      to={`/jobs/${job.id}`}
                      className={`status-edge ${TONE_EDGE[tone]} flex items-center gap-4 p-4 transition-colors hover:bg-bg-subtle`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-text-primary">
                          {job.job_name || job.file_upload?.original_filename || 'Unnamed job'}
                        </p>
                        <p className="font-data mt-0.5 truncate text-xs text-text-tertiary">
                          {new Date(job.created_at).toLocaleString()}
                        </p>
                      </div>
                      <JobStatusBadge status={job.status} size="sm" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section aria-labelledby="go-heading">
        <h2 id="go-heading" className="sr-only">
          Shortcuts
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {QUICK_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="group rounded-lg border border-border bg-bg-surface p-4 transition-colors hover:bg-bg-subtle"
            >
              <link.icon className="mb-2 h-5 w-5 text-text-secondary" aria-hidden="true" />
              <p className="font-medium text-text-primary">{link.label}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
